import * as THREE from 'three'
import { useRef, useState, useMemo, useEffect } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { Image, ScrollControls, Scroll, useScroll, useVideoTexture, Html } from '@react-three/drei'
import { proxy, useSnapshot } from 'valtio'
import { easing } from 'maath'
import { useSectionsStore } from './store'
import { SectionOverlay } from './SectionOverlay'
import { ProjectInfo } from './ProjectInfo'

// Fix image paths for Vite - using public directory intiate number of images and videos 
import img1 from '/images/1.jpg'
import img3 from '/images/3.jpg'
import img4 from '/images/4.jpg'
import img5 from '/images/5.jpg'
import img6 from '/images/6.jpg'
import img7 from '/images/7.jpg'
import img8 from '/images/8.jpg'
import img9 from '/images/9.jpg'
import img10 from '/images/10.jpg'
import img11 from '/images/11.jpg'    
import img12 from '/images/12.jpg'
import img13 from '/images/13.jpg'


// Video
const videoSrc = '/videos/gns-intro-web.mp4'
const videoSrc2 = '/videos/CandiesGen2.mp4'
const videoSrc3 = '/videos/Arrival.mp4'
const videoSrc4 = '/videos/AI7.mp4'
const videoSrc5 = '/videos/LORAAI7.mp4'
const videoSrc6 = '/videos/DubaiToken2049.mp4' // 13
const videoSrc7 = '/videos/Goddess.mp4' // 14 
const videoSrc8 = '/videos/Creatures.mp4' // 15
const videoSrc9 = '/videos/Apple.mp4' // 16
const videoSrc10 = '/videos/Passage.mp4' // 17
const videoSrc11 = '/videos/CubeEditor.mp4' // 
const videoSrc12 = '/videos/Tripo3D.mp4' // 18
const videoSrc13 = '/videos/Freepik.mp4' // 19
const videoSrc14 = '/videos/GlitchHunter.mp4' // 20

// Helper function to detect if a file is a video
const isVideoFile = (src) => {
  if (typeof src !== 'string') return false
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v']
  return videoExtensions.some(ext => src.toLowerCase().includes(ext))
}

// Helper function to detect animated images (GIF, animated WebP)
const isAnimatedImage = (src) => {
  if (typeof src !== 'string') return false
  const animatedExtensions = ['.gif', '.webp']
  return animatedExtensions.some(ext => src.toLowerCase().includes(ext))
}

// Helper function to validate video file exists and is accessible
const validateVideoFile = async (src) => {
  try {
    const response = await fetch(src, { method: 'HEAD' })
    return response.ok && response.headers.get('content-type')?.startsWith('video/')
  } catch {
    return false
  }
}

const images = [img1, videoSrc2, img3, img5, videoSrc4, img6, videoSrc12, img8, videoSrc10, img10, videoSrc11, img12, videoSrc, videoSrc6, videoSrc7, videoSrc8, videoSrc3, videoSrc9, videoSrc13, videoSrc14]

// Accent color for UI elements
const ACCENT_COLOR = '#64ffda'

// Create materials with the accent color
const minimapMaterial = new THREE.LineBasicMaterial({ color: ACCENT_COLOR })
const minimapGeometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, -0.5, 0), new THREE.Vector3(0, 0.5, 0)])

const state = proxy({
  clicked: null,
  urls: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map((u, i) => {
    // Get the source from images array
    const source = images[(u - 1) % 20] // change to number of images (now 20)
    // Determine the type based on file extension
    let type = 'image'
    if (isVideoFile(source)) {
      type = 'video'
    } else if (isAnimatedImage(source)) {
      type = 'animated-image'
    }
    return { 
      src: source, 
      type: type
    }
  })
})

// Track scroll position globally
const scrollState = proxy({
  position: 0,
  currentIndex: 0
})

// Export state for use in other components
export { state }

function Minimap() {
  const ref = useRef()
  const scroll = useScroll()
  const { urls } = useSnapshot(state)
  const { height } = useThree((state) => state.viewport)
  
  useFrame((state, delta) => {
    if (!ref.current) return
    ref.current.children.forEach((child, index) => {
      // Give me a value between 0 and 1
      //   starting at the position of my item
      //   ranging across 4 / total length
      //   make it a sine, so the value goes from 0 to 1 to 0.
      const y = scroll.curve(index / urls.length - 1.5 / urls.length, 4 / urls.length)
      easing.damp(child.scale, 'y', 0.15 + y / 6, 0.15, delta)
    })
  })
  
  return (
    <group ref={ref}>
      {urls.map((_, i) => (
        <line 
          key={i} 
          geometry={minimapGeometry} 
          material={minimapMaterial} 
          position={[i * 0.06 - urls.length * 0.03, -height / 2 + 0.6, 0]} 
        />
      ))}
    </group>
  )
}

function VideoItem({ index, position, scale, ...props }) {
  const ref = useRef()
  const scroll = useScroll()
  const { clicked, urls } = useSnapshot(state)
  const [hovered, hover] = useState(false)
  const { getSectionForIndex, setActiveSection, isGloballyMuted } = useSectionsStore()
  const [playing, setPlaying] = useState(false)
  const [textureError, setTextureError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [autoPlay, setAutoPlay] = useState(false) // For auto-playing when in view
  const [hasAudio, setHasAudio] = useState(false) // Track if this video should have audio
  const [userInteracted, setUserInteracted] = useState(false) // Track if user has interacted
  const playPromiseRef = useRef(null) // Track ongoing play promises
  const videoStateRef = useRef('idle') // Track video state: 'idle', 'playing', 'pausing', 'transitioning'
  const pendingActionRef = useRef(null) // Track pending actions
  
  // Setup a video texture - hooks must be called unconditionally
  const texture = useVideoTexture(props.url, {
    muted: true, // Always start muted to prevent browser tab muting
    loop: true,
    playsInline: true,
    crossOrigin: 'anonymous'
  })
  
  // Enhanced safe play function with state management
  const safePlay = async (video, withAudio = false) => {
    // If already playing or transitioning, handle audio state change
    if (videoStateRef.current === 'playing' || videoStateRef.current === 'transitioning') {
      if (withAudio && !isGloballyMuted && userInteracted) {
        video.muted = false
        setHasAudio(true)
      } else if (!withAudio || isGloballyMuted) {
        video.muted = true
        setHasAudio(false)
      }
      return
    }
    
    try {
      videoStateRef.current = 'transitioning'
      
      // Set audio state BEFORE playing to ensure it's in the same call stack as user interaction
      if (withAudio && !isGloballyMuted && userInteracted) {
        video.muted = false
        setHasAudio(true)
      } else {
        video.muted = true
        setHasAudio(false)
      }
      
      // Ensure looping is always enabled
      video.loop = true
      
      // Wait for any existing operations to complete
      if (playPromiseRef.current) {
        try {
          await playPromiseRef.current
        } catch (error) {
          // Ignore errors from previous operations
        }
        playPromiseRef.current = null
      }
      
      // Check if video is already playing
      if (!video.paused) {
        videoStateRef.current = 'playing'
        return
      }
      
      // Start play operation with audio state already set
      playPromiseRef.current = video.play()
      await playPromiseRef.current
      
      videoStateRef.current = 'playing'
      playPromiseRef.current = null
      
    } catch (error) {
      videoStateRef.current = 'idle'
      playPromiseRef.current = null
      
      // Only log non-abort errors
      if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
        console.error(`Error playing video ${props.url}:`, error)
      }
    }
  }
  
  // Enhanced safe pause function
  const safePause = async (video) => {
    // If already paused or transitioning, ignore
    if (videoStateRef.current === 'idle' || videoStateRef.current === 'transitioning') {
      return
    }
    
    try {
      videoStateRef.current = 'transitioning'
      
      // Wait for any ongoing play operation
      if (playPromiseRef.current) {
        try {
          await playPromiseRef.current
        } catch (error) {
          // Play was interrupted, video should be paused already
          videoStateRef.current = 'idle'
          playPromiseRef.current = null
          return
        }
        playPromiseRef.current = null
      }
      
      // Pause the video
      if (!video.paused) {
        video.pause()
      }
      
      videoStateRef.current = 'idle'
      
    } catch (error) {
      videoStateRef.current = 'idle'
      console.error(`Error pausing video ${props.url}:`, error)
    }
  }
  
  // Queue actions to prevent conflicts
  const queueVideoAction = (action, withAudio = false, immediate = false) => {
    // For user-initiated actions with audio, execute immediately to maintain call stack
    if (immediate || (withAudio && userInteracted)) {
      if (texture?.image) {
        if (action === 'play') {
          safePlay(texture.image, withAudio)
        } else if (action === 'pause') {
          safePause(texture.image)
        }
      }
      return
    }
    
    // For auto-play and other actions, use the queue to prevent conflicts
    pendingActionRef.current = { action, withAudio }
    
    // Execute after a short delay to batch rapid calls
    setTimeout(() => {
      if (pendingActionRef.current?.action === action && texture?.image) {
        if (action === 'play') {
          safePlay(texture.image, withAudio)
        } else if (action === 'pause') {
          safePause(texture.image)
        }
        pendingActionRef.current = null
      }
    }, 50)
  }
  
  // Handle texture loading states
  useEffect(() => {
    if (!texture || !texture.image) return
    
    const video = texture.image
    
    const handleLoadStart = () => setIsLoading(true)
    const handleLoadedData = () => setIsLoading(false)
    const handleError = (error) => {
      console.error(`Video texture error for ${props.url}:`, error)
      setTextureError(true)
      setIsLoading(false)
    }
    
    video.addEventListener('loadstart', handleLoadStart)
    video.addEventListener('loadeddata', handleLoadedData)
    video.addEventListener('error', handleError)
    
    // Check if video is already loaded
    if (video.readyState >= 2) {
      setIsLoading(false)
    }
    
    return () => {
      video.removeEventListener('loadstart', handleLoadStart)
      video.removeEventListener('loadeddata', handleLoadedData)
      video.removeEventListener('error', handleError)
    }
  }, [texture, props.url])
  
  // Handle audio state based on clicked status and global mute
  useEffect(() => {
    if (!texture || !texture.image) return
    
    const video = texture.image
    const isCurrentlyClicked = clicked === index
    
    // Only manage audio state if this video is currently playing and user has interacted
    if (!playing || !userInteracted) return
    
    // Don't interfere if globally muted (handled by global mute effect)
    if (isGloballyMuted) return
    
    // Only handle switching audio OFF when video is no longer clicked
    // (Don't handle switching audio ON - that's handled by click action)
    if (!isCurrentlyClicked && hasAudio) {
      video.muted = true
      setHasAudio(false)
    }
  }, [clicked, index, texture, hasAudio, isGloballyMuted, playing, userInteracted])
  
  // Handle global mute changes
  useEffect(() => {
    if (!texture || !texture.image) return
    
    const video = texture.image
    
    if (isGloballyMuted) {
      // Global mute overrides everything - mute all videos
      video.muted = true
      setHasAudio(false)
    } else if (clicked === index && playing && userInteracted) {
      // When global mute is disabled, restore audio for clicked and playing videos that user interacted with
      video.muted = false
      setHasAudio(true)
    }
  }, [isGloballyMuted, clicked, index, playing, texture, userInteracted])
  
  // Handle click events
  const click = () => {
    if (textureError || isLoading) return // Don't allow clicks on errored or loading videos
    
    const wasClicked = state.clicked
    state.clicked = index === clicked ? null : index
    
    // Toggle video playback - allow multiple videos to play
    if (texture && texture.image) {
      if (index === clicked && wasClicked === index) {
        // Clicking again - toggle play/pause
        if (playing) {
          setPlaying(false)
          queueVideoAction('pause', false, true) // immediate = true for user action
        } else {
          setPlaying(true)
          setUserInteracted(true) // Mark user interaction
          // Unmute when user explicitly plays (unless globally muted) - IMMEDIATE execution
          queueVideoAction('play', !isGloballyMuted, true) // immediate = true for user action
        }
      } else if (state.clicked === index) {
        // First click - start playing with audio (if not globally muted)
        setPlaying(true)
        setUserInteracted(true) // Mark user interaction
        // Unmute when user clicks to view (unless globally muted) - IMMEDIATE execution
        queueVideoAction('play', !isGloballyMuted, true) // immediate = true for user action
      }
    }

    // Update active section based on clicked image
    const section = getSectionForIndex(index)
    if (section) {
      setActiveSection(section.id)
    }
  }
  
  const over = () => hover(true)
  const out = () => hover(false)
  
  // Auto-play when video comes into view - simplified and more stable
  useFrame((state, delta) => {
    if (!ref.current || !texture || !texture.image) return
    
    const y = scroll.curve(index / urls.length - 1.5 / urls.length, 4 / urls.length)
    
    // Simple threshold-based autoplay without rapid state changes
    if (y > 0.4 && !autoPlay && !textureError) {
      setAutoPlay(true)
      // Only auto-play if user hasn't manually interacted with this video
      if (!userInteracted) {
        setPlaying(true)
        // Keep muted for autoplay but ensure looping
        texture.image.muted = true
        texture.image.loop = true
        queueVideoAction('play', false) // false = keep muted for autoplay
      }
    } else if (y <= 0.05 && autoPlay) {
      setAutoPlay(false)
      // Don't auto-pause if user manually started the video
      if (!clicked || clicked !== index || !userInteracted) {
        setPlaying(false)
        queueVideoAction('pause')
        // Mute when auto-pausing
        texture.image.muted = true
        setHasAudio(false)
      }
    }
    
    // Visual animations
    easing.damp3(ref.current.scale, [clicked === index ? 4.7 : scale[0], clicked === index ? 5 : 4 + y, 1], 0.15, delta)
    if (clicked !== null && index < clicked) easing.damp(ref.current.position, 'x', position[0] - 2, 0.15, delta)
    if (clicked !== null && index > clicked) easing.damp(ref.current.position, 'x', position[0] + 2, 0.15, delta)
    if (clicked === null || clicked === index) easing.damp(ref.current.position, 'x', position[0], 0.15, delta)
    easing.damp(ref.current.material, 'grayscale', hovered || clicked === index ? 0 : Math.max(0, 1 - y), 0.15, delta)
    easing.dampC(ref.current.material.color, hovered || clicked === index ? 'white' : '#aaa', hovered ? 0.3 : 0.15, delta)
  })
  
  // Monitor video state and ensure continuous playback
  useEffect(() => {
    if (!texture || !texture.image || textureError || !playing) return
    
    const video = texture.image
    
    const handleEnded = () => {
      // If video ends but should be playing, restart it
      if (playing && video.loop) {
        const shouldHaveAudio = userInteracted && clicked === index && !isGloballyMuted
        queueVideoAction('play', shouldHaveAudio)
      }
    }
    
    const handlePause = () => {
      // If video pauses but should be playing (and not due to user action), restart it
      if (playing && videoStateRef.current === 'playing') {
        const shouldHaveAudio = userInteracted && clicked === index && !isGloballyMuted
        queueVideoAction('play', shouldHaveAudio)
      }
    }
    
    video.addEventListener('ended', handleEnded)
    video.addEventListener('pause', handlePause)
    
    return () => {
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('pause', handlePause)
    }
  }, [texture, textureError, playing, clicked, index, isGloballyMuted, userInteracted])
  
  // If there's a texture error, render as a regular image instead
  if (textureError) {
    console.warn(`Falling back to image rendering for ${props.url}`)
    return <Item index={index} position={position} scale={scale} url={props.url} />
  }
  
  return (
    <mesh
      ref={ref}
      position={position}
      scale={scale}
      onClick={click}
      onPointerOver={over}
      onPointerOut={out}
    >
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial 
        map={texture} 
        transparent 
        toneMapped={false} 
        color={isLoading ? '#666' : (hasAudio && !isGloballyMuted ? '#64ffda' : 'white')} 
        opacity={isLoading ? 0.5 : 1}
      />
    </mesh>
  )
}

function Item({ index, position, scale, c = new THREE.Color(), ...props }) {
  const ref = useRef()
  const scroll = useScroll()
  const { clicked, urls } = useSnapshot(state)
  const [hovered, hover] = useState(false)
  const { getSectionForIndex, setActiveSection } = useSectionsStore()
  
  const click = () => {
    state.clicked = index === clicked ? null : index
    // Update active section based on clicked image
    const section = getSectionForIndex(index)
    if (section) {
      setActiveSection(section.id)
    }
  }
  
  const over = () => hover(true)
  const out = () => hover(false)
  
  useFrame((state, delta) => {
    if (!ref.current) return
    const y = scroll.curve(index / urls.length - 1.5 / urls.length, 4 / urls.length)
    easing.damp3(ref.current.scale, [clicked === index ? 4.7 : scale[0], clicked === index ? 5 : 4 + y, 1], 0.15, delta)
    ref.current.material.scale[0] = ref.current.scale.x
    ref.current.material.scale[1] = ref.current.scale.y
    if (clicked !== null && index < clicked) easing.damp(ref.current.position, 'x', position[0] - 2, 0.15, delta)
    if (clicked !== null && index > clicked) easing.damp(ref.current.position, 'x', position[0] + 2, 0.15, delta)
    if (clicked === null || clicked === index) easing.damp(ref.current.position, 'x', position[0], 0.15, delta)
    easing.damp(ref.current.material, 'grayscale', hovered || clicked === index ? 0 : Math.max(0, 1 - y), 0.15, delta)
    easing.dampC(ref.current.material.color, hovered || clicked === index ? 'white' : '#aaa', hovered ? 0.3 : 0.15, delta)
  })
  return <Image ref={ref} {...props} position={position} scale={scale} onClick={click} onPointerOver={over} onPointerOut={out} />
}

// Safe wrapper for VideoItem that handles errors gracefully
function SafeVideoItem({ index, position, scale, url, ...props }) {
  const [hasError, setHasError] = useState(false)
  const [isValidVideo, setIsValidVideo] = useState(true)
  
  useEffect(() => {
    // Validate video file on mount
    const checkVideo = async () => {
      const isValid = await validateVideoFile(url)
      if (!isValid) {
        console.warn(`Invalid or inaccessible video file: ${url}`)
        setIsValidVideo(false)
        setHasError(true)
      }
    }
    
    if (isVideoFile(url)) {
      checkVideo()
    }
  }, [url])
  
  // If there's an error or invalid video, render as image
  if (hasError || !isValidVideo) {
    return <Item index={index} position={position} scale={scale} url={url} {...props} />
  }
  
  // Try to render as video, with error boundary
  try {
    return <VideoItem index={index} position={position} scale={scale} url={url} {...props} />
  } catch (error) {
    console.error(`Error rendering video item for ${url}:`, error)
    setHasError(true)
    return <Item index={index} position={position} scale={scale} url={url} {...props} />
  }
}

// Scroll tracker component to update sections
function ScrollTracker() {
  const scroll = useScroll()
  const { urls } = useSnapshot(state)
  const { setActiveSection, sections, setCurrentIndex, setHorizontalProgress, shouldNavigateToStudio, shouldNavigateToStart, resetStudioNavigation, resetStartNavigation, shouldTransitionToSplash, setTransitioningToSplash, enterStudioSection, leaveStudioSection, resetStudioViewed } = useSectionsStore()
  const [lastIndex, setLastIndex] = useState(-1)
  const clickedState = useSnapshot(state).clicked
  const progressRef = useRef()
  const progressBgRef = useRef()
  const { width, height } = useThree((state) => state.viewport)
  const hasTriggeredSplash = useRef(false)

  useFrame(() => {
    if (scroll && typeof scroll.offset === 'number') {
      // Handle programmatic navigation to Studio
      if (shouldNavigateToStudio && scroll.el) {
        // Directly set scroll to trigger Studio section (95% of the way through)
        const targetScrollLeft = scroll.el.scrollWidth * 0.95;
        scroll.el.scrollLeft = targetScrollLeft;
        resetStudioNavigation();
        return;
      }

      // Handle programmatic navigation back to start
      if (shouldNavigateToStart && scroll.el) {
        // Scroll to the very beginning (index 0) to ensure we're at the first slide
        // This will be before section 1 starts (section 1 starts at index 2)
        const targetScrollLeft = scroll.el.scrollWidth * 0.001; // Very beginning
        scroll.el.scrollLeft = targetScrollLeft;
        resetStartNavigation();
        return;
      }

      // Store scroll position globally
      scrollState.position = scroll.offset
      
      // Update horizontal scroll progress in the store - key for syncing multiple components:
      // 1. Controls video visibility (shown when horizontalProgress <= 0.15 forward, <= 0.08 backward)
      // 2. Controls Studio section slide-up (starts sliding at horizontalProgress >= 0.85, index 17)
      setHorizontalProgress(scroll.offset)
      
      // Track Studio section entry/exit
      if (scroll.offset >= 0.9) {
        enterStudioSection();
      } else if (scroll.offset < 0.85) {
        leaveStudioSection();
      }
      
      // Update clicked state
      scrollState.clicked = clickedState
      
      // Update progress bar width based on scroll offset
      if (progressRef.current) {
        // Calculate the full width
        const fullWidth = width
        // Set scale to represent current position
        progressRef.current.scale.x = scroll.offset * fullWidth
      }
      
      // Calculate current image index based on scroll position
      const currentIndex = Math.min(
        Math.floor(scroll.offset * urls.length),
        urls.length - 1
      )
      
      // Update current index in the store for startIndex enforcement
      setCurrentIndex(currentIndex)
      
      // Reset splash transition flag if we're no longer at the beginning
      if (currentIndex > 1 || scroll.offset > 0.03) {
        setTransitioningToSplash(false);
        hasTriggeredSplash.current = false; // Reset when moving away from beginning
      }
      
      // Check if we should transition back to splash screen - but don't interfere with scrolling
      if (shouldTransitionToSplash() && !hasTriggeredSplash.current) {
        console.log('Should transition back to splash screen');
        hasTriggeredSplash.current = true;
        setTransitioningToSplash(true);
        // Don't call window.scrollTo as it interferes with horizontal scrolling
        // The splash screen transition should be handled by the vertical scroll progress
        return;
      }
      
      // Only process if the index has changed
      if (currentIndex !== lastIndex) {
        setLastIndex(currentIndex)
        scrollState.currentIndex = currentIndex
        
        // Determine which section we're currently viewing
        let activeSection = null
        for (const section of sections) {
          if (currentIndex >= section.startIndex && currentIndex <= section.endIndex) {
            activeSection = section.id
            break
          }
        }
        
        // Update active section if found
        if (activeSection !== null) {
          setActiveSection(activeSection)
          // console.log(`Active section: ${activeSection}, currentIndex: ${currentIndex}, horizontalProgress: ${scroll.offset.toFixed(3)}`);
        } else {
          // We're in the pre-section area (indices 0-1)
          // console.log(`Pre-section area: currentIndex: ${currentIndex}, horizontalProgress: ${scroll.offset.toFixed(3)}`);
          
          // Reset Studio viewed flag when reaching the very beginning
          if (currentIndex === 0 && scroll.offset < 0.02) {
            resetStudioViewed();
            console.log('Reset Studio viewed flag - back at beginning');
          }
        }
      }
    }
  })
  
  return (
    <group position={[0, -height / 2 + 0.1, 0]}>
      {/* Background bar - gray track */}
      <mesh 
        ref={progressBgRef}
        scale={[width, 0.03, 1]}
        position={[width/2, 0, 0]}
      >
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial color="#333333" transparent opacity={0.5} />
      </mesh>
      
      {/* Progress indicator - accent color */}
      <mesh 
        ref={progressRef} 
        scale={[0, 0.03, 1]}
        position={[0, 0, 0.01]} // Slightly in front
      >
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial color={ACCENT_COLOR} transparent opacity={0.9} />
      </mesh>
    </group>
  )
}

function Items({ w = 0.7, gap = 0.15 }) {
  const { urls } = useSnapshot(state)
  const { width } = useThree((state) => state.viewport)
  const xW = w + gap
  
  // Reset horizontal scroll position on mount to ensure we start at the beginning
  useEffect(() => {
    // Reset any stored scroll state
    scrollState.position = 0
    scrollState.currentIndex = 0
    state.clicked = null
  }, [])
  
  return (
    <ScrollControls horizontal damping={0.1} pages={(width - xW + urls.length * xW) / width}>
      <ScrollTracker />
      <SectionOverlay />
      <Minimap />
      <Scroll>
        {urls.map((item, i) => {
          if (item.type === 'video') {
            return <SafeVideoItem key={i} index={i} position={[i * xW, 0, 0]} scale={[w, 4, 1]} url={item.src} />
          } else {
            // Handle both regular images and animated images (GIF, WebP) as regular images
            // Note: Animated images will play automatically as they're treated as images by the browser
            return <Item key={i} index={i} position={[i * xW, 0, 0]} scale={[w, 4, 1]} url={item.src} />
          }
        })}
      </Scroll>
    </ScrollControls>
  )
}

export const App = () => (
  <Canvas gl={{ antialias: false }} dpr={[1, 1.5]} onPointerMissed={() => (state.clicked = null)}>
    <Items />
  </Canvas>
)
